import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  BarChart,
} from 'recharts';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import {
  BudgetedVsTargetByCategoryData,
  defaultBudgetItemsConfig,
} from '../../../../../../../types/apiTypes';
import { Select } from 'antd';
import { unicodeToEmoji } from '../../../../../../../shared/emojiUnicode';
import { ClipLoader } from 'react-spinners';
import { formatNumberCommas } from '../../../../../../../utils/CurrencyUtils';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';

const budgetCategories = [
  ...Object.values(defaultBudgetItemsConfig).map((category) => ({
    value: category?.category,
    label: category?.category_display_name,
    emoji: category?.emoji_unicode,
  })),
  {
    value: 'all',
    label: 'All Categories',
    emoji: 'U+1F4C2',
  },
];

function BudgetVsActualExpensesWidget({
  addClass,
  budgetedVsTargetData,
  loadingBudgetedVsTargetData,
  selectedCategory,
  setSelectedCategory,
  selectedUnitNumber,
  isPreActive,
}: {
  addClass?: string;
  budgetedVsTargetData: BudgetedVsTargetByCategoryData[];
  loadingBudgetedVsTargetData: boolean;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  selectedUnitNumber?: string;
  isPreActive: boolean;
}) {
  const { screenView } = useScreenView();
  const budgetedItemsWithTarget =
    budgetedVsTargetData?.filter((item) => item?.target > 0) || [];
  const averageHistoricalTargetAmount =
    budgetedItemsWithTarget.length > 0
      ? budgetedItemsWithTarget.reduce((sum, item) => sum + item?.target, 0) /
        budgetedItemsWithTarget.length
      : 0;
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <DemoDataWrapper showBadge={isPreActive}>
            <div
              className={`flex flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
            >
              <div className="flex h-[71px] max-h-[71px] w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
                <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                  Budget vs Actual Expenses by Category
                  {selectedUnitNumber && (
                    <span className="block text-sm font-normal text-graysecondary">
                      {selectedUnitNumber}
                    </span>
                  )}
                </h2>
                <Select
                  value={selectedCategory}
                  onChange={(value) => setSelectedCategory(value)}
                  className="min-w-[200px] disabled:cursor-not-allowed disabled:opacity-60"
                  placeholder="Select Category"
                  disabled={isPreActive}
                >
                  {budgetCategories.map((category) => (
                    <Select.Option
                      key={category?.value}
                      value={category?.value}
                    >
                      {unicodeToEmoji(category?.emoji)} {category?.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {!loadingBudgetedVsTargetData &&
                budgetedVsTargetData &&
                budgetedVsTargetData?.length > 0 && (
                  <div className="flex h-[90px] w-full flex-row items-start overflow-x-auto border-b-[1px] border-[#EAECF0] align-middle">
                    <div className="flex w-full flex-col items-start gap-y-2 p-3 align-middle">
                      <p className="text-[16px] text-[#7D8191]">
                        {' '}
                        Average Historical Monthly Budget{' '}
                      </p>
                      <p className="text-[22px] font-semibold leading-[28px] text-primary">
                        ${formatNumberCommas(averageHistoricalTargetAmount)}.
                        <span className="text-[18px] leading-[28px] text-graysecondary">
                          {((averageHistoricalTargetAmount % 1) * 100)
                            .toFixed(0)
                            .padStart(2, '0')}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              {!loadingBudgetedVsTargetData ? (
                <>
                  {budgetedVsTargetData && budgetedVsTargetData?.length > 0 ? (
                    <div className="flex h-[360px] max-h-[360px] min-h-[360px] w-full flex-col py-2">
                      <div className="flex flex-row items-center space-x-4 px-4 py-2 align-middle text-[16px] text-[#7D8191]">
                        <div className="h-4 w-4 rounded-sm bg-[#73a2bd]"></div>
                        <p>Target</p>
                        <div className="h-4 w-4 rounded-sm bg-[#013366]"></div>
                        <p>Actual</p>
                      </div>
                      <ResponsiveContainer width="100%" height={380}>
                        <BarChart
                          data={budgetedVsTargetData}
                          layout="horizontal"
                          margin={{ right: 20, left: 20 }}
                        >
                          {/* Y Axis represents categories */}
                          <YAxis type="number" />

                          {/* X Axis represents the value range */}
                          <XAxis
                            type="category"
                            dataKey="month"
                            tickFormatter={(monthIndex) => {
                              const date = new Date(0); // Create a date object
                              date.setUTCMonth(monthIndex); // Set the month (0-based index)
                              return date.toLocaleString('default', {
                                month: 'short',
                              }); // Format to short month
                            }}
                          />
                          <XAxis dataKey="target" xAxisId={0} hide />
                          <XAxis dataKey="actual" xAxisId={1} hide />
                          {/* Tooltip */}
                          <Tooltip content={<CustomTooltip />} />

                          <Bar
                            dataKey="target"
                            fill="#73a2bd"
                            barSize={30}
                            opacity={0.5}
                            style={{ zIndex: 1 }}
                            radius={[4, 4, 0, 0]}
                            xAxisId={0}
                          />

                          {/* Top Layer (Actual) - narrower bar on top */}
                          <Bar
                            dataKey="actual"
                            fill="#013366"
                            barSize={20}
                            style={{ zIndex: 50 }}
                            radius={[4, 4, 0, 0]}
                            xAxisId={1}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[327px] w-full flex-col items-center justify-center p-4 text-center">
                      <p className="text-[16px] font-semibold leading-[24px] text-primary">
                        Compare Budgeted and Actual Expenses
                      </p>
                      <p className="text-[14px] leading-[20px] text-graysecondary">
                        See how your spending aligns with your planned budget
                        for each category.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-[427px] min-h-[427px] w-full items-center justify-center">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingBudgetedVsTargetData}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="flex h-full w-full items-center justify-center"
                  />
                </div>
              )}
            </div>
          </DemoDataWrapper>
        );
      case 'tablet':
        return (
          <DemoDataWrapper showBadge={isPreActive}>
            <div
              className={`flex flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
            >
              <div className="flex h-[71px] max-h-[71px] w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
                <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                  Budget vs Actual
                  {selectedUnitNumber && (
                    <span className="block text-sm font-normal text-graysecondary">
                      {selectedUnitNumber}
                    </span>
                  )}
                </h2>
                <Select
                  value={selectedCategory}
                  onChange={(value) => setSelectedCategory(value)}
                  className="min-w-[140px] disabled:cursor-not-allowed disabled:opacity-60"
                  placeholder="Select Category"
                  disabled={isPreActive}
                >
                  {budgetCategories.map((category) => (
                    <Select.Option
                      key={category?.value}
                      value={category?.value}
                    >
                      {unicodeToEmoji(category?.emoji)} {category?.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {!loadingBudgetedVsTargetData &&
                budgetedVsTargetData &&
                budgetedVsTargetData?.length > 0 && (
                  <div className="flex h-[90px] w-full flex-row items-center overflow-x-auto overflow-y-hidden border-b-[1px] border-[#EAECF0] align-middle">
                    <div className="flex w-full flex-col justify-center gap-y-1 px-2 py-1 align-middle">
                      <p className="text-[16px] text-[#7D8191]">
                        {' '}
                        Average Historical Monthly Budget{' '}
                      </p>
                      <p className="text-[22px] font-semibold leading-[28px] text-primary">
                        ${formatNumberCommas(averageHistoricalTargetAmount)}.
                        <span className="text-[18px] text-graysecondary">
                          {((averageHistoricalTargetAmount % 1) * 100)
                            .toFixed(0)
                            .padStart(2, '0')}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              {!loadingBudgetedVsTargetData ? (
                <>
                  {budgetedVsTargetData && budgetedVsTargetData?.length > 0 ? (
                    <div className="flex h-[337px] max-h-[400px] min-h-[337px] w-full flex-col py-2">
                      <div className="flex flex-row items-center space-x-4 px-4 py-2 align-middle text-[16px] text-[#7D8191]">
                        <div className="h-4 w-4 rounded-sm bg-[#73a2bd]"></div>
                        <p>Target</p>
                        <div className="h-4 w-4 rounded-sm bg-[#013366]"></div>
                        <p>Actual</p>
                      </div>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart
                          data={budgetedVsTargetData}
                          layout="horizontal"
                          margin={{ right: 20, left: 20 }}
                        >
                          {/* Y Axis represents categories */}
                          <YAxis type="number" />

                          {/* X Axis represents the value range */}
                          <XAxis
                            type="category"
                            dataKey="month"
                            tickFormatter={(monthIndex) => {
                              const date = new Date(0); // Create a date object
                              date.setUTCMonth(monthIndex); // Set the month (0-based index)
                              return date.toLocaleString('default', {
                                month: 'short',
                              }); // Format to short month
                            }}
                          />
                          <XAxis dataKey="target" xAxisId={0} hide />
                          <XAxis dataKey="actual" xAxisId={1} hide />
                          {/* Tooltip */}
                          <Tooltip content={<CustomTooltip />} />

                          <Bar
                            dataKey="target"
                            fill="#73a2bd"
                            barSize={30}
                            opacity={0.5}
                            style={{ zIndex: 1 }}
                            radius={[4, 4, 0, 0]}
                            xAxisId={0}
                          />

                          {/* Top Layer (Actual) - narrower bar on top */}
                          <Bar
                            dataKey="actual"
                            fill="#013366"
                            barSize={20}
                            style={{ zIndex: 50 }}
                            radius={[4, 4, 0, 0]}
                            xAxisId={1}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[327px] w-full flex-col items-center justify-center p-4 text-center">
                      <p className="text-[16px] font-semibold leading-[24px] text-primary">
                        Compare Budgeted and Actual Expenses
                      </p>
                      <p className="text-[14px] leading-[20px] text-graysecondary">
                        See how your spending aligns with your planned budget
                        for each category.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-[427px] min-h-[427px]">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingBudgetedVsTargetData}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="my-10 flex h-full w-full items-center justify-center"
                  />
                </div>
              )}
            </div>
          </DemoDataWrapper>
        );
      case 'mobile':
        return (
          <DemoDataWrapper showBadge={isPreActive}>
            <div
              className={`flex flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
            >
              <div className="flex h-[71px] max-h-[71px] w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
                <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                  Budget vs Actual
                  {selectedUnitNumber && (
                    <span className="block text-sm font-normal text-graysecondary">
                      {selectedUnitNumber}
                    </span>
                  )}
                </h2>
                <Select
                  value={selectedCategory}
                  onChange={(value) => setSelectedCategory(value)}
                  className="min-w-[140px] disabled:cursor-not-allowed disabled:opacity-60"
                  placeholder="Select Category"
                  disabled={isPreActive}
                >
                  {budgetCategories.map((category) => (
                    <Select.Option
                      key={category?.value}
                      value={category?.value}
                    >
                      {unicodeToEmoji(category?.emoji)} {category?.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {!loadingBudgetedVsTargetData &&
                budgetedVsTargetData &&
                budgetedVsTargetData?.length > 0 && (
                  <div className="flex h-[90px] w-full flex-row items-center overflow-x-auto overflow-y-hidden border-b-[1px] border-[#EAECF0] align-middle">
                    <div className="flex w-full flex-col justify-center gap-y-1 px-2 py-1 align-middle">
                      <p className="text-[16px] text-[#7D8191]">
                        {' '}
                        Average Historical Monthly Budget{' '}
                      </p>
                      <p className="text-[22px] font-semibold leading-[28px] text-primary">
                        ${formatNumberCommas(averageHistoricalTargetAmount)}.
                        <span className="text-[18px] text-graysecondary">
                          {((averageHistoricalTargetAmount % 1) * 100)
                            .toFixed(0)
                            .padStart(2, '0')}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              {!loadingBudgetedVsTargetData ? (
                <>
                  {budgetedVsTargetData && budgetedVsTargetData?.length > 0 ? (
                    <div className="flex h-[337px] max-h-[400px] min-h-[337px] w-full flex-col py-2">
                      <div className="flex flex-row items-center space-x-4 px-4 py-2 align-middle text-[16px] text-[#7D8191]">
                        <div className="h-4 w-4 rounded-sm bg-[#73a2bd]"></div>
                        <p>Target</p>
                        <div className="h-4 w-4 rounded-sm bg-[#013366]"></div>
                        <p>Actual</p>
                      </div>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart
                          data={budgetedVsTargetData}
                          layout="horizontal"
                          margin={{ right: 20, left: 20 }}
                        >
                          {/* Y Axis represents categories */}
                          <YAxis type="number" />

                          {/* X Axis represents the value range */}
                          <XAxis
                            type="category"
                            dataKey="month"
                            tickFormatter={(monthIndex) => {
                              const date = new Date(0); // Create a date object
                              date.setUTCMonth(monthIndex); // Set the month (0-based index)
                              return date.toLocaleString('default', {
                                month: 'short',
                              }); // Format to short month
                            }}
                          />
                          <XAxis dataKey="target" xAxisId={0} hide />
                          <XAxis dataKey="actual" xAxisId={1} hide />
                          {/* Tooltip */}
                          <Tooltip content={<CustomTooltip />} />

                          <Bar
                            dataKey="target"
                            fill="#73a2bd"
                            barSize={30}
                            opacity={0.5}
                            style={{ zIndex: 1 }}
                            radius={[4, 4, 0, 0]}
                            xAxisId={0}
                          />

                          {/* Top Layer (Actual) - narrower bar on top */}
                          <Bar
                            dataKey="actual"
                            fill="#013366"
                            barSize={20}
                            style={{ zIndex: 50 }}
                            radius={[4, 4, 0, 0]}
                            xAxisId={1}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[327px] w-full flex-col items-center justify-center p-4 text-center">
                      <p className="text-[16px] font-semibold leading-[24px] text-primary">
                        Compare Budgeted and Actual Expenses
                      </p>
                      <p className="text-[14px] leading-[20px] text-graysecondary">
                        See how your spending aligns with your planned budget
                        for each category.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-[427px] min-h-[427px]">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingBudgetedVsTargetData}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="my-10 flex h-full w-full items-center justify-center"
                  />
                </div>
              )}
            </div>
          </DemoDataWrapper>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default BudgetVsActualExpensesWidget;

const CustomTooltip = ({ active, payload, label }: any) => {
  const date = new Date(0); // Create a date object
  date.setUTCMonth(label); // Set the month (0-based index)
  const month = date.toLocaleString('default', { month: 'short' });
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
        <p className="text-[14px] font-semibold">{month}.</p>
        <div className="flex items-center">
          <span
            className="mr-2 inline-block h-3 w-3"
            style={{ backgroundColor: '#73a2bd' }}
          ></span>
          <p className="text-[14px]">
            Target: ${payload[0]?.value?.toLocaleString()}
          </p>
        </div>
        <div className="flex items-center">
          <span
            className="mr-2 inline-block h-3 w-3 border-[1px] border-white"
            style={{ backgroundColor: '#013366' }}
          ></span>
          <p className="text-[14px]">
            Actual: ${payload[1]?.value?.toLocaleString()}
          </p>
        </div>
      </div>
    );
  }
  return null;
};
